import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, downloadPlan, printPlan, openFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClipboardText, Plus, Trash, CheckCircle, UserCircle, MapPin, FilePdf, Printer, ClockCounterClockwise, ImageSquare, PencilSimple } from "@phosphor-icons/react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");
const blankItem = { material_id: "", quantity: "", place: "" };

export default function ProductionPlans() {
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const canEdit = hasPerm("production-plans", "edit");
  const canEditPlans = hasPerm("plan-edit", "edit");
  const confirm = useConfirm();
  const [plans, setPlans] = useState([]);
  const [planFilter, setPlanFilter] = useState("all");
  const [materials, setMaterials] = useState([]);
  const [managers, setManagers] = useState([]);
  const [open, setOpen] = useState(false);
  const [manager, setManager] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([{ ...blankItem }]);
  const [saving, setSaving] = useState(false);
  const [completeItem, setCompleteItem] = useState(null); // {plan, idx, item, remaining}
  const [goodQty, setGoodQty] = useState("");
  const [scrapQty, setScrapQty] = useState("");
  const [scrapReason, setScrapReason] = useState("");
  const [completing, setCompleting] = useState(false);
  const [editRow, setEditRow] = useState(null); // {plan, idx, item}
  const [editMat, setEditMat] = useState("");
  const [editQty, setEditQty] = useState("");
  const [editPlace, setEditPlace] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const load = () => {
    api.get("/production-plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {});
    api.get("/managers").then((r) => setManagers(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const addManager = async () => {
    const name = window.prompt("New floor manager name:");
    if (!name || !name.trim()) return;
    try {
      const res = await api.post("/managers", { name: name.trim() });
      toast.success("Manager added");
      setManagers((prev) => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setManager(res.data.name);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const cancel = async (plan) => {
    if (!(await confirm({ title: "Cancel Plan", description: `Cancel ${plan.plan_number}? It will be marked cancelled and closed.`, confirmText: "Cancel Plan" }))) return;
    try { await api.post(`/production-plans/${plan.id}/cancel`); toast.success("Plan cancelled"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openNew = () => { setManager(""); setNotes(""); setItems([{ ...blankItem }]); setOpen(true); };

  const save = async () => {
    if (!manager.trim()) return toast.error("Assign a production manager");
    const clean = items
      .filter((i) => i.material_id && Number(i.quantity) > 0)
      .map((i) => ({ material_id: i.material_id, quantity: Number(i.quantity), place: i.place }));
    if (clean.length === 0) return toast.error("Add at least one material with a quantity");
    setSaving(true);
    try {
      await api.post("/production-plans", { manager, notes, items: clean });
      toast.success("Production plan created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openComplete = (plan, idx, item, remaining) => {
    setCompleteItem({ plan, idx, item, remaining });
    setGoodQty("");
    setScrapQty("");
    setScrapReason("");
    setCompleting(false);
  };

  const submitComplete = async () => {
    if (!completeItem) return;
    const good = goodQty === "" ? 0 : Number(goodQty);
    const scrap = scrapQty === "" ? 0 : Number(scrapQty);
    if (good + scrap <= 0) return toast.error("Enter a good and/or scrapped quantity");
    setCompleting(true);
    try {
      const res = await api.post(`/production-plans/${completeItem.plan.id}/items/${completeItem.idx}/complete`, {
        completed_qty: good,
        scrapped_qty: scrap,
        scrap_reason: scrapReason.trim() || null,
      });
      const partial = res.data?.item_status === "partial";
      const scrapMsg = scrap > 0 ? ` · ${scrap} scrapped (recorded as SCRAP)` : "";
      toast.success((partial ? "Recorded — good qty added to stock, item still open" : "Item completed — good qty added to raw material stock") + scrapMsg);
      setCompleteItem(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setCompleting(false);
    }
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Delete Plan", description: "Delete this production plan? This cannot be undone.", confirmText: "Delete" }))) return;
    try { await api.delete(`/production-plans/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openEditRow = (plan, idx, item) => {
    setEditRow({ plan, idx, item });
    setEditMat(item.material_id);
    setEditQty(String(item.quantity));
    setEditPlace(item.place || "");
    setSavingEdit(false);
  };

  const submitEditRow = async () => {
    if (!editRow) return;
    if (!editMat) return toast.error("Select a material");
    if (!(Number(editQty) > 0)) return toast.error("Enter a quantity greater than 0");
    setSavingEdit(true);
    try {
      await api.put(`/production-plans/${editRow.plan.id}/items/${editRow.idx}`, {
        material_id: editMat,
        quantity: Number(editQty),
        place: editPlace,
      });
      toast.success("Plan row updated");
      setEditRow(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <>
      <PageHeader title="Production Plans" subtitle="In-House Manufacturing">
        {canEdit && (<Button onClick={openNew} data-testid="add-plan-button" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider transition-colors duration-100">
          <Plus size={16} weight="bold" className="mr-1" /> New Plan
        </Button>)}
      </PageHeader>

      <div className="p-4 sm:p-8 space-y-4" data-testid="plans-list">
        {plans.length > 0 && (() => {
          const openCount = plans.filter((p) => p.status === "open").length;
          const fc = (f) => f === "all" ? plans.length : f === "open" ? openCount : plans.filter((p) => p.status === f).length;
          const PF = [
            { key: "all", label: "All" },
            { key: "open", label: "Open / In Progress" },
            { key: "completed", label: "Completed" },
            { key: "cancelled", label: "Cancelled" },
          ];
          return (
            <div className="flex flex-wrap gap-2" data-testid="plan-filter-bar">
              {PF.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setPlanFilter(f.key)}
                  data-testid={`plan-filter-${f.key}`}
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors duration-100 ${planFilter === f.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"}`}
                >
                  {f.label} <span className="font-mono opacity-70">({fc(f.key)})</span>
                </button>
              ))}
            </div>
          );
        })()}
        {plans.length === 0 && (
          <div className="bg-white border border-zinc-300 p-12 text-center text-zinc-400">
            <ClipboardText size={32} weight="thin" className="mx-auto mb-3" /> No production plans yet.
          </div>
        )}
        {plans.length > 0 && plans.filter((p) => planFilter === "all" ? true : p.status === planFilter).length === 0 && (
          <div className="bg-white border border-zinc-300 p-12 text-center text-zinc-400" data-testid="plan-filter-empty">
            No {planFilter === "open" ? "open" : planFilter} production plans.
          </div>
        )}
        {plans.filter((p) => planFilter === "all" ? true : p.status === planFilter).map((plan) => (
          <div key={plan.id} className="bg-white border border-zinc-300" data-testid={`plan-card-${plan.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-zinc-300 bg-zinc-50">
              <div className="flex items-center gap-3">
                <span className="font-mono font-bold text-sm bg-zinc-900 text-white px-2 py-1">{plan.plan_number}</span>
                <div className="flex items-center gap-2 text-sm text-zinc-700">
                  <UserCircle size={18} weight="bold" className="text-primary" />
                  <span className="font-medium">{plan.manager}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase text-white ${plan.status === "completed" ? "bg-[#16A34A]" : plan.status === "cancelled" ? "bg-zinc-500" : "bg-[#0891B2]"}`}>
                  {plan.status === "completed" ? "Completed" : plan.status === "cancelled" ? "Cancelled" : "In Progress"}
                </span>
                {plan.status !== "completed" && plan.status !== "cancelled" && (
                  <button onClick={() => cancel(plan)} data-testid={`cancel-plan-${plan.id}`} title="Cancel plan" className="text-xs font-bold uppercase text-zinc-500 hover:text-[#DC2626] px-1 transition-colors duration-100">Cancel</button>
                )}
                <button onClick={() => navigate(`/production-plans/${plan.id}/timeline`)} data-testid={`plan-timeline-${plan.id}`} title="View timeline" className="text-zinc-600 hover:text-primary p-2 transition-colors duration-100"><ClockCounterClockwise size={18} weight="bold" /></button>
                <button onClick={() => downloadPlan(plan.id, plan.plan_number).catch(() => toast.error("Download failed"))} data-testid={`download-plan-${plan.id}`} title="Download work order PDF" className="text-zinc-600 hover:text-primary p-2 transition-colors duration-100"><FilePdf size={18} weight="bold" /></button>
                <button onClick={() => printPlan(plan.id).catch(() => toast.error("Print failed"))} data-testid={`print-plan-${plan.id}`} title="Print work order" className="text-zinc-600 hover:text-primary p-2 transition-colors duration-100"><Printer size={18} weight="bold" /></button>
                <button onClick={() => remove(plan.id)} className="text-[#DC2626] p-2"><Trash size={16} weight="bold" /></button>
              </div>
            </div>
            {(() => {
              const planned = plan.items.reduce((s, it) => s + (it.quantity || 0), 0);
              const made = plan.items.reduce((s, it) => s + (it.completed_qty || 0), 0);
              const scrapped = plan.items.reduce((s, it) => s + (it.scrapped_qty || 0), 0);
              const pct = planned > 0 ? Math.min(100, Math.round((made / planned) * 100)) : 0;
              const madePct = planned > 0 ? (made / planned) * 100 : 0;
              const scrapPct = planned > 0 ? (scrapped / planned) * 100 : 0;
              return (
                <div className="px-6 pt-4">
                  <div className="flex justify-between text-[11px] uppercase tracking-wide text-zinc-500 mb-1">
                    <span>Progress</span>
                    <span className="font-mono" data-testid={`plan-progress-${plan.id}`}>
                      {made} made{scrapped > 0 && <span className="text-[#DC2626]"> · {scrapped} scrap</span>} / {planned} ({pct}%)
                    </span>
                  </div>
                  <div className="flex h-2 bg-zinc-200 w-full overflow-hidden">
                    <div className={`h-full ${plan.status === "cancelled" ? "bg-zinc-400" : pct === 100 && scrapped === 0 ? "bg-[#16A34A]" : "bg-[#0891B2]"}`} style={{ width: `${madePct}%`, transition: "width 300ms ease" }} title={`Made ${made}`} />
                    <div className="h-full bg-[#DC2626]" style={{ width: `${scrapPct}%`, transition: "width 300ms ease" }} title={`Scrap ${scrapped}`} />
                  </div>
                  {scrapped > 0 && (
                    <div className="flex gap-4 mt-1.5 text-[11px] text-zinc-500">
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-[#0891B2] inline-block" /> Made</span>
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-[#DC2626] inline-block" /> Scrap</span>
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-zinc-200 inline-block" /> Remaining</span>
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="p-4 sm:p-6 overflow-x-auto">
              <table className="w-full text-sm min-w-[420px]">
                <thead>
                  <tr className="text-left text-xs uppercase text-zinc-500 border-b border-zinc-200">
                    <th className="py-2 font-bold">Material to Manufacture</th>
                    <th className="py-2 font-bold text-right">Qty</th>
                    <th className="py-2 font-bold text-right">Made</th>
                    <th className="py-2 font-bold">Place</th>
                    <th className="py-2 font-bold text-right">Status / Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {plan.items.map((it, i) => {
                    const made = it.completed_qty || 0;
                    const scrapped = it.scrapped_qty || 0;
                    const remaining = Math.max(it.quantity - made - scrapped, 0);
                    const partial = it.status !== "completed" && (made > 0 || scrapped > 0);
                    const noActivity = made === 0 && scrapped === 0 && !(it.history?.length);
                    return (
                      <tr key={`${it.material_id}-${i}`} data-testid={`plan-item-${plan.id}-${i}`}>
                        <td className="py-3 text-zinc-800">{it.material_name}</td>
                        <td className="py-3 text-right font-mono">{it.quantity} {it.unit}</td>
                        <td className={`py-3 text-right font-mono ${partial ? "text-[#0891B2] font-bold" : it.status === "completed" ? "text-[#16A34A]" : "text-zinc-400"}`}>
                          {made} {it.unit}{partial ? ` (${remaining} left)` : ""}
                          {scrapped > 0 && <div className="text-[11px] font-bold text-[#DC2626]" data-testid={`plan-item-scrap-${plan.id}-${i}`}>✕ {scrapped} {it.unit} scrap</div>}
                        </td>
                        <td className="py-3 text-zinc-600">
                          <span className="inline-flex items-center gap-1"><MapPin size={13} weight="bold" className="text-zinc-400" />{it.place || "—"}</span>
                          {(it.history?.length ?? 0) > 0 && (
                            <div className="mt-2 space-y-1" data-testid={`plan-item-history-${plan.id}-${i}`}>
                              {it.history.map((h, hi) => (
                                <div key={`hist-${hi}`} className="text-[11px] text-zinc-500">
                                  {h.qty > 0 && (
                                    <div className="flex items-center gap-1.5">
                                      <ClockCounterClockwise size={12} weight="bold" className="text-[#0891B2]" />
                                      <span className="font-mono text-zinc-700">+{h.qty} {it.unit}</span>
                                      <span className="text-zinc-400">·</span>
                                      <span>{fmt(h.at)}</span>
                                      <span className="text-zinc-400">·</span>
                                      <span>{h.by_name}</span>
                                    </div>
                                  )}
                                  {h.scrapped > 0 && (
                                    <div className="flex items-center gap-1.5 text-[#DC2626]">
                                      <span className="font-mono font-bold">✕ {h.scrapped} {it.unit} scrap</span>
                                      {h.reason && <><span className="text-zinc-400">·</span><span className="italic">{h.reason}</span></>}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="py-3">
                          {plan.status === "cancelled" ? (
                            <span className="text-xs text-zinc-400 italic">Cancelled</span>
                          ) : it.status === "completed" ? (
                            <div className="flex items-center justify-end gap-2 text-[#16A34A]">
                              <CheckCircle size={16} weight="fill" />
                              <span className="text-xs font-mono">{it.completed_qty} {it.unit} made</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-2">
                              {canEditPlans && noActivity && plan.status !== "cancelled" && (
                                <button onClick={() => openEditRow(plan, i, it)} data-testid={`edit-plan-row-${plan.id}-${i}`} title="Edit material & quantity" className="text-zinc-500 hover:text-primary p-1.5 transition-colors duration-100"><PencilSimple size={16} weight="bold" /></button>
                              )}
                              <Button size="sm" onClick={() => openComplete(plan, i, it, remaining)} data-testid={`complete-item-${plan.id}-${i}`} className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white text-xs uppercase font-bold">
                                <CheckCircle size={14} weight="bold" className="mr-1" /> {partial ? "Add More" : "Report Done"}
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {plan.notes && <p className="text-xs text-zinc-500 mt-3">Note: {plan.notes}</p>}
              <p className="text-[11px] text-zinc-400 mt-2">Planned by {plan.by_name} · {fmt(plan.created_at)}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Report completion + rejections dialog */}
      <Dialog open={!!completeItem} onOpenChange={(v) => { if (!v) setCompleteItem(null); }}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-md" data-testid="complete-item-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Report Production</DialogTitle></DialogHeader>
          {completeItem && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-zinc-500">
                {completeItem.item.material_name} · remaining <span className="font-mono text-[#EA580C]">{completeItem.remaining}</span> {completeItem.item.unit}
              </p>
              <p className="text-[11px] text-zinc-400">Good qty is added to raw material stock. Scrapped qty is non-repairable — it is <b>not</b> added to stock and is recorded separately as SCRAP.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase text-[#16A34A]">Good Qty</Label>
                  <Input type="number" min="0" max={completeItem.remaining} placeholder="0" data-testid="complete-good-qty" value={goodQty} onChange={(e) => setGoodQty(e.target.value)} className="rounded-none border-zinc-300 font-mono text-right focus-visible:ring-2 focus-visible:ring-[#16A34A]" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase text-[#DC2626]">Scrapped Qty</Label>
                  <Input type="number" min="0" max={completeItem.remaining} placeholder="0" data-testid="complete-scrap-qty" value={scrapQty} onChange={(e) => setScrapQty(e.target.value)} className="rounded-none border-zinc-300 font-mono text-right focus-visible:ring-2 focus-visible:ring-[#DC2626]" />
                </div>
              </div>
              {Number(scrapQty) > 0 && (
                <div className="space-y-1" data-testid="scrap-reason-block">
                  <Label className="text-[10px] font-bold uppercase text-[#DC2626]">Scrap Reason</Label>
                  <Input placeholder="e.g. cracked / warped / burnt" data-testid="scrap-reason-input" value={scrapReason} onChange={(e) => setScrapReason(e.target.value)} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#DC2626]" />
                  <p className="text-[11px] text-zinc-400">Scrapped pieces close this portion of the plan (not remade).</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteItem(null)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={submitComplete} disabled={completing} data-testid="complete-item-confirm" className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white font-bold uppercase tracking-wider">
              <CheckCircle size={16} weight="bold" className="mr-1" /> {completing ? "Saving…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-2xl" data-testid="plan-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">New Production Plan</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2 max-h-[55vh] overflow-y-auto">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Production Manager Assigned *</Label>
              <div className="flex items-center gap-2">
                <Select value={manager} onValueChange={setManager}>
                  <SelectTrigger data-testid="plan-manager-select" className="rounded-none border-zinc-300 flex-1 focus:ring-2 focus:ring-primary">
                    <SelectValue placeholder={managers.length ? "Select floor manager" : "No managers yet — add one"} />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {managers.map((mg) => <SelectItem key={mg.id} value={mg.name}>{mg.name}{mg.area ? ` · ${mg.area}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" onClick={addManager} data-testid="add-manager-button" className="rounded-none border-zinc-300 text-xs uppercase font-bold whitespace-nowrap">
                  <Plus size={14} weight="bold" className="mr-1" /> New
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Items to Manufacture</Label>
              {items.map((it, i) => (
                <div key={`planline-${it.material_id || "new"}-${i}`} className="flex items-center gap-2">
                  <Select value={it.material_id} onValueChange={(v) => { const n = [...items]; n[i].material_id = v; setItems(n); }}>
                    <SelectTrigger data-testid={`plan-material-${i}`} className="rounded-none border-zinc-300 flex-1 focus:ring-2 focus:ring-primary"><SelectValue placeholder="Raw material" /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" placeholder="Qty" data-testid={`plan-qty-${i}`} value={it.quantity} onChange={(e) => { const n = [...items]; n[i].quantity = e.target.value; setItems(n); }} className="rounded-none border-zinc-300 font-mono w-24 focus-visible:ring-2 focus-visible:ring-primary" />
                  <Input placeholder="Place of manufacturing" data-testid={`plan-place-${i}`} value={it.place} onChange={(e) => { const n = [...items]; n[i].place = e.target.value; setItems(n); }} className="rounded-none border-zinc-300 w-48 focus-visible:ring-2 focus-visible:ring-primary" />
                  <button onClick={() => setItems(items.filter((_, x) => x !== i))} className="text-[#DC2626] p-1"><Trash size={16} weight="bold" /></button>
                </div>
              ))}
              <Button variant="outline" onClick={() => setItems([...items, { ...blankItem }])} data-testid="plan-add-line" className="rounded-none border-zinc-300 text-xs uppercase font-bold">
                <Plus size={14} weight="bold" className="mr-1" /> Add Item
              </Button>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Notes for the floor</Label>
              <Textarea data-testid="plan-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="plan-save-button" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">{saving ? "Saving…" : "Create Plan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit plan row dialog */}
      <Dialog open={!!editRow} onOpenChange={(o) => { if (!o) setEditRow(null); }}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-md" data-testid="edit-plan-row-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Edit Plan Row</DialogTitle></DialogHeader>
          {editRow && (
            <div className="space-y-4 py-2">
              <p className="text-[11px] text-zinc-400 border-l-2 border-primary pl-2">
                {editRow.plan.plan_number} · Row {editRow.idx + 1}. Only rows with no production activity can be edited.
              </p>
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-wider">Material to Manufacture</Label>
                <Select value={editMat} onValueChange={setEditMat}>
                  <SelectTrigger data-testid="edit-plan-material" className="rounded-none border-zinc-300 focus:ring-2 focus:ring-primary"><SelectValue placeholder="Raw material" /></SelectTrigger>
                  <SelectContent className="rounded-none max-h-72">
                    {materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs font-bold uppercase tracking-wider">Quantity</Label>
                  <Input type="number" min="1" data-testid="edit-plan-qty" value={editQty} onChange={(e) => setEditQty(e.target.value)} className="rounded-none border-zinc-300 font-mono text-right focus-visible:ring-2 focus-visible:ring-primary" />
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs font-bold uppercase tracking-wider">Place</Label>
                  <Input data-testid="edit-plan-place" value={editPlace} onChange={(e) => setEditPlace(e.target.value)} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={submitEditRow} disabled={savingEdit} data-testid="edit-plan-row-confirm" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">
              {savingEdit ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
